import { useState } from "react";
import { Button } from "../../components/Button";
import { Field } from "../../components/Field";
import { PhoneInput } from "../../components/PhoneInput";
import { ParentSurface } from "./ParentSurface";
import { DEFAULT_COUNTRY, normalisePhone, type CountryDialInfo } from "../../lib/phone";

/** What the parent confirmed — exactly one of the two is set. */
export type ChildIdentifier = { childPhone: string } | { childEmail: string };

interface ParentProgressGateProps {
  onSubmit: (known: ChildIdentifier) => void;
  checking: boolean;
  error: string | null;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The gate in front of a child's scores.
 *
 * The SMS link alone does not open this. Links get forwarded and screenshotted,
 * and one that by itself showed a named child's exam performance would be
 * exactly the disclosure NDPA is written to prevent — so a parent confirms
 * something they already know about the child: their phone number, or their
 * email for a child who signed up without a phone.
 *
 * Worded as a confirmation rather than a challenge: a parent who has just been
 * texted a link should not feel accused of anything.
 */
export function ParentProgressGate({ onSubmit, checking, error }: ParentProgressGateProps) {
  const [mode, setMode] = useState<"phone" | "email">("phone");
  const [country, setCountry] = useState<CountryDialInfo>(DEFAULT_COUNTRY);
  const [digits, setDigits] = useState("");
  const [email, setEmail] = useState("");

  const trimmedEmail = email.trim().toLowerCase();
  const ready =
    mode === "phone" ? normalisePhone(digits, country) !== null : EMAIL_PATTERN.test(trimmedEmail);

  const submit = () => {
    if (mode === "email") {
      if (EMAIL_PATTERN.test(trimmedEmail)) onSubmit({ childEmail: trimmedEmail });
      return;
    }
    const phone = normalisePhone(digits, country);
    if (phone) onSubmit({ childPhone: phone });
  };

  return (
    <ParentSurface>
      <h1 className="m-0 pt-8 font-[family-name:var(--font-display)] text-[22px] font-bold leading-[1.25]">
        Just checking it's you.
      </h1>
      <p className="m-0 pt-3 text-[14px] leading-[1.6] text-text-muted">
        Enter your child's phone number or email — the one they use for PrepAI — and we'll show you
        how they're getting on.
      </p>

      <div
        role="group"
        aria-label="How you know your child"
        className="mt-6 flex gap-1 rounded-[var(--radius-chip)] border border-border bg-surface p-1"
      >
        {(["phone", "email"] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={mode === option}
            onClick={() => setMode(option)}
            className={`min-h-10 flex-1 rounded-[var(--radius-chip)] px-3 text-[12.5px] font-semibold transition-colors ${
              mode === option ? "bg-accent-tint text-text" : "text-text-muted"
            }`}
          >
            {option === "phone" ? "Phone number" : "Email"}
          </button>
        ))}
      </div>

      <div className="pt-5">
        {mode === "phone" ? (
          <Field label="Your child's phone number" htmlFor="child-phone" error={error}>
            <PhoneInput
              id="child-phone"
              value={digits}
              onChange={setDigits}
              country={country}
              onCountryChange={setCountry}
              invalid={Boolean(error)}
            />
          </Field>
        ) : (
          <Field label="Your child's email" htmlFor="child-email" error={error}>
            <input
              id="child-email"
              type="email"
              inputMode="email"
              autoComplete="off"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              aria-invalid={Boolean(error)}
              placeholder="child@example.com"
              className={`min-h-12 w-full rounded-[var(--radius-control)] border bg-surface px-3.5 text-[15px] text-text outline-none placeholder:text-text-muted focus:border-accent ${
                error ? "border-negative" : "border-border-strong"
              }`}
            />
          </Field>
        )}
      </div>

      <div className="mt-auto flex flex-col gap-3 pt-8">
        <Button disabled={!ready || checking} onClick={submit}>
          {checking ? "Checking…" : "Show me their progress"}
        </Button>
        <p className="m-0 text-center text-[12.5px] leading-[1.5] text-text-muted">
          We ask because this page shows your child's exam scores. Only someone who knows their
          details should see them.
        </p>
      </div>
    </ParentSurface>
  );
}
